import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Event } from './events.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatChipsModule } from '@angular/material/chips';

import { 
  formatUTCToLocalWithTimezone, 
  formatDateShort,
  getTimezoneAbbreviation,
  getUserTimezone 
} from '../shared/timezone.utils';

interface DialogData {
  event: Event;
  canModify: boolean;
  isAuthenticated: boolean;
}

@Component({
  selector: 'app-event-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatDialogModule,
    MatButtonModule,
    MatChipsModule
  ],
  template: `
    <div class="event-details-dialog">
      <!-- Header -->
      <div class="dialog-header">
        <div class="header-content">
          <mat-icon class="header-icon">event_available</mat-icon>
          <h2>Event Details</h2>
        </div>
        <button mat-icon-button (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Content -->
      <mat-dialog-content class="dialog-content">
        <!-- Event Title -->
        <div class="section">
          <label>Event Title</label>
          <h3 class="event-title">{{ data.event.title }}</h3>
        </div>

        <!-- Event Image -->
        <div class="event-image" *ngIf="data.event.image" (click)="openLightbox()">
          <img [src]="data.event.image" [alt]="data.event.title">
          <div class="zoom-hint">
            <mat-icon>zoom_in</mat-icon>
            <span>Click to zoom</span>
          </div>
        </div>

        <!-- Event Details Grid -->
        <div class="details-grid">
          <div class="detail-item">
            <label>Start Date & Time</label>
            <div class="detail-value">
              <mat-icon>calendar_today</mat-icon>
              <span>{{ formatDateTime(data.event.start) }}</span>
            </div>
          </div>

          <div class="detail-item">
            <label>End Date & Time</label>
            <div class="detail-value">
              <mat-icon>calendar_today</mat-icon>
              <span>{{ formatDateTime(data.event.end) }}</span>
            </div>
          </div>

          <div class="detail-item">
            <label>Event Type</label>
            <div class="detail-value">
              <mat-icon>category</mat-icon>
              <span class="capitalize">{{ data.event.type }}</span>
            </div>
          </div>

          <div class="detail-item">
            <label>Platform</label>
            <div class="detail-value">
              <mat-icon>videocam</mat-icon>
              <span>{{ data.event.platform }}</span>
            </div>
          </div>

          <!-- Location -->
          <div class="detail-item" *ngIf="data.event.location">
            <label>Location</label>
            <div class="detail-value">
              <mat-icon>place</mat-icon>
              <span>{{ data.event.location }}</span>
            </div>
          </div>

          <!-- Max Attendees -->
          <div class="detail-item" *ngIf="data.event.maxAttendees">
            <label>Max Attendees</label>
            <div class="detail-value">
              <mat-icon>people</mat-icon>
              <span>{{ data.event.maxAttendees }}</span>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="section description" *ngIf="data.event.description">
          <label>Description</label>
          <p>{{ data.event.description }}</p>
        </div>

        <!-- Target Participants -->
        <div class="section" *ngIf="data.event.participants && data.event.participants.length > 0">
          <label>Target Participants</label>
          <div class="chips-container">
            <mat-chip-set>
              <mat-chip *ngFor="let participant of data.event.participants" class="participant-chip">
                <mat-icon>person</mat-icon>
                <span class="capitalize">{{ participant }}</span>
              </mat-chip>
            </mat-chip-set>
          </div>
        </div>

        <!-- Tags -->
        <div class="section" *ngIf="data.event.tags && data.event.tags.length > 0">
          <label>Tags</label>
          <div class="chips-container">
            <mat-chip-set>
              <mat-chip *ngFor="let tag of data.event.tags" class="tag-chip">
                <mat-icon>label</mat-icon>
                {{ tag }}
              </mat-chip>
            </mat-chip-set>
          </div>
        </div>

        <!-- Meeting/Registration URL -->
        <div class="section" *ngIf="data.event.meetingUrl">
          <label>Registration/Meeting Link</label>
          <div class="detail-value link-value">
            <mat-icon>link</mat-icon>
            <a [href]="data.event.meetingUrl" target="_blank" rel="noopener noreferrer">
              {{ data.event.meetingUrl }}
            </a>
          </div>
        </div>

        <!-- Event Metadata -->
        <div class="section metadata" *ngIf="data.event.createdAt">
          <label>Event Information</label>
          <div class="metadata-grid">
            <div class="metadata-item" *ngIf="data.event.createdAt">
              <mat-icon>schedule</mat-icon>
              <span>Created: {{ formatDate(data.event.createdAt) }}</span>
            </div>
            <div class="metadata-item" *ngIf="data.event.updatedAt">
              <mat-icon>update</mat-icon>
              <span>Last Updated: {{ formatDate(data.event.updatedAt) }}</span>
            </div>
          </div>
        </div>

        <!-- User Timezone Info -->
        <div class="section timezone-info">
          <mat-icon>info</mat-icon>
          <span>Times displayed in your local timezone ({{ getTimezone() }})</span>
        </div>
      </mat-dialog-content>

      <!-- Actions -->
      <mat-dialog-actions class="dialog-actions">
        <div class="actions-grid">
          <a 
            *ngIf="data.event.meetingUrl"
            [href]="data.event.meetingUrl"
            target="_blank"
            rel="noopener noreferrer"
            mat-raised-button
            color="primary"
            class="register-button">
            <mat-icon>launch</mat-icon>
            Register Now
          </a>

          <button
            *ngIf="data.canModify"
            mat-raised-button
            color="accent"
            (click)="editEvent()">
            <mat-icon>edit</mat-icon>
            Edit Event
          </button>

          <button
            *ngIf="data.canModify"
            mat-raised-button
            class="delete-button"
            (click)="deleteEvent()">
            <mat-icon>delete</mat-icon>
            Delete Event
          </button>
        </div>
      </mat-dialog-actions>
    </div>

    <!-- Lightbox / Zoom Overlay -->
    <div class="lightbox-overlay" *ngIf="isLightboxOpen" (click)="closeLightbox()">
      <button mat-icon-button class="lightbox-close" (click)="closeLightbox()">
        <mat-icon>close</mat-icon>
      </button>

      <div class="lightbox-controls" (click)="$event.stopPropagation()">
        <button mat-icon-button (click)="zoomOut()" [disabled]="zoomLevel <= minZoom">
          <mat-icon>zoom_out</mat-icon>
        </button>
        <span class="zoom-level">{{ (zoomLevel * 100).toFixed(0) }}%</span>
        <button mat-icon-button (click)="zoomIn()" [disabled]="zoomLevel >= maxZoom">
          <mat-icon>zoom_in</mat-icon>
        </button>
        <button mat-icon-button (click)="resetZoom()">
          <mat-icon>restart_alt</mat-icon>
        </button>
      </div>

      <div class="lightbox-image-wrapper" (click)="$event.stopPropagation()" (wheel)="onWheelZoom($event)">
        <img 
          [src]="data.event.image" 
          [alt]="data.event.title"
          [style.transform]="'scale(' + zoomLevel + ')'"
          class="lightbox-image">
      </div>
    </div>
  `,
  styles: [`
    .event-details-dialog {
      display: flex;
      flex-direction: column;
      max-height: 90vh;
      width: 100%;
    }

    .dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: linear-gradient(135deg, #007664 0%, #005a4a 100%);
      color: white;
      padding: 1.5rem 2rem;
      margin: -24px -24px 0 -24px;

      .header-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;

        h2 {
          margin: 0;
          font-size: 1.75rem;
          font-weight: 600;
        }

        .header-icon {
          font-size: 2rem;
          width: 2rem;
          height: 2rem;
          color: #a7f3d0;
        }
      }

      button {
        color: white;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }
      }
    }

    .dialog-content {
      flex: 1;
      overflow-y: auto;
      padding: 2rem;
    }

    .section {
      margin-bottom: 1.5rem;

      label {
        display: block;
        font-size: 0.875rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.75rem;
      }

      &.description {
        border-top: 1px solid #e5e7eb;
        padding-top: 1.5rem;

        p {
          white-space: pre-wrap;
          color: #374151;
          line-height: 1.6;
          margin: 0;
        }
      }

      &.metadata {
        border-top: 1px solid #e5e7eb;
        padding-top: 1.5rem;
      }

      &.timezone-info {
        background-color: #f0fdf4;
        border: 1px solid #dcfce7;
        border-radius: 0.375rem;
        padding: 1rem;
        display: flex;
        align-items: center;
        gap: 0.75rem;
        color: #166534;
        margin-top: 2rem;
        border-top: none;
        padding-top: 1rem;

        mat-icon {
          color: #16a34a;
          font-size: 1.25rem;
          width: 1.25rem;
          height: 1.25rem;
          flex-shrink: 0;
        }

        span {
          font-size: 0.875rem;
          font-weight: 500;
        }
      }
    }

    .event-title {
      font-size: 1.75rem;
      font-weight: 700;
      color: #111827;
      margin: 0;
      text-transform: capitalize;
    }

    .event-image {
      position: relative;
      width: 100%;
      height: 16rem;
      border-radius: 0.5rem;
      overflow: hidden;
      margin-bottom: 1.5rem;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      background-color: #f3f4f6;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      .zoom-hint {
        position: absolute;
        bottom: 0.75rem;
        right: 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.35rem;
        background-color: rgba(0, 0, 0, 0.6);
        color: white;
        padding: 0.35rem 0.75rem;
        border-radius: 1rem;
        font-size: 0.75rem;
        opacity: 0;
        transition: opacity 0.2s;

        mat-icon {
          font-size: 1rem;
          width: 1rem;
          height: 1rem;
        }
      }

      &:hover .zoom-hint {
        opacity: 1;
      }
    }

    .details-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1.25rem;
      margin-bottom: 1.5rem;

      @media (max-width: 600px) {
        grid-template-columns: 1fr;
      }
    }

    .detail-item {
      label {
        display: block;
        font-weight: 600;
        color: #6b7280;
        margin-bottom: 0.5rem;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .detail-value {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #111827;
        font-size: 0.95rem;

        mat-icon {
          color: #007664;
          font-size: 1.25rem;
          width: 1.25rem;
          height: 1.25rem;
          flex-shrink: 0;
        }

        .capitalize {
          text-transform: capitalize;
        }
      }
    }

    .link-value {
      a {
        color: #007664;
        text-decoration: none;
        word-break: break-all;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .chips-container {
      mat-chip-set {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      mat-chip {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        font-size: 0.875rem;

        mat-icon {
          font-size: 1rem;
          width: 1rem;
          height: 1rem;
        }

        &.participant-chip {
          background-color: #dbeafe;
          color: #1e40af;

          mat-icon {
            color: #1e40af;
          }
        }

        &.tag-chip {
          background-color: #f3e8ff;
          color: #7c3aed;

          mat-icon {
            color: #7c3aed;
          }
        }

        .capitalize {
          text-transform: capitalize;
        }
      }
    }

    .metadata-grid {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .metadata-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #6b7280;
      font-size: 0.875rem;

      mat-icon {
        font-size: 1rem;
        width: 1rem;
        height: 1rem;
        color: #9ca3af;
      }
    }

    .dialog-actions {
      padding: 1.5rem 2rem;
      border-top: 1px solid #e5e7eb;
      background-color: #f9fafb;
    }

    .actions-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 1rem;
      width: 100%;

      button, a {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        font-weight: 500;
      }

      .register-button {
        background-color: #007664 !important;
        color: white !important;

        &:hover {
          background-color: #005a4d !important;
        }
      }

      .delete-button {
        background-color: #dc2626 !important;
        color: white !important;

        &:hover {
          background-color: #b91c1c !important;
        }
      }
    }

    .capitalize {
      text-transform: capitalize;
    }

    @media (max-width: 600px) {
      .actions-grid {
        grid-template-columns: 1fr;
      }
    }

    .dialog-content::-webkit-scrollbar {
      width: 6px;
    }

    .dialog-content::-webkit-scrollbar-track {
      background: #f1f5f9;
    }

    .dialog-content::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;

      &:hover {
        background: #94a3b8;
      }
    }

    /* Lightbox styles */
    .lightbox-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.9);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: zoom-out;
    }

    .lightbox-close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      color: white;
      background-color: rgba(255, 255, 255, 0.1);
      z-index: 2002;

      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
    }

    .lightbox-controls {
      position: absolute;
      bottom: 1.5rem;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background-color: rgba(0, 0, 0, 0.6);
      padding: 0.5rem 1rem;
      border-radius: 2rem;
      z-index: 2002;
      cursor: default;

      button {
        color: white;

        &:disabled {
          color: rgba(255, 255, 255, 0.3);
        }
      }

      .zoom-level {
        color: white;
        font-size: 0.875rem;
        min-width: 3rem;
        text-align: center;
      }
    }

    .lightbox-image-wrapper {
      width: 90vw;
      height: 80vh;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      cursor: default;
    }

    .lightbox-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      transition: transform 0.15s ease-out;
    }
  `]
})
export class EventDetailsDialogComponent {
  isLightboxOpen = false;
  zoomLevel = 1;
  readonly minZoom = 0.5;
  readonly maxZoom = 3;
  readonly zoomStep = 0.25;

  constructor(
    public dialogRef: MatDialogRef<EventDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {}

  formatDateTime(dateString: string): string {
    try {
      return formatUTCToLocalWithTimezone(dateString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  formatDate(dateString: string): string {
    try {
      return formatDateShort(dateString);
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  }

  getTimezone(): string {
    return getTimezoneAbbreviation();
  }

  openLightbox(): void {
    this.isLightboxOpen = true;
    this.zoomLevel = 1;
  }

  closeLightbox(): void {
    this.isLightboxOpen = false;
    this.zoomLevel = 1;
  }

  zoomIn(): void {
    this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom);
  }

  zoomOut(): void {
    this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
  }

  resetZoom(): void {
    this.zoomLevel = 1;
  }

  onWheelZoom(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  editEvent(): void {
    this.dialogRef.close({ action: 'edit', event: this.data.event });
  }

  deleteEvent(): void {
    this.dialogRef.close({ action: 'delete', event: this.data.event });
  }

  close(): void {
    this.dialogRef.close();
  }
}